import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const MAP_STYLE = {
  display: 'block',
  width: '100%',
  maxWidth: 640,
  aspectRatio: '4 / 3',
  margin: '0 auto',
  borderRadius: 8,
};

const UNAVAILABLE_STYLE = {
  ...MAP_STYLE,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#f0f0f0',
};

const buttonStyle = {
  display: 'block',
  margin: '8px auto 0',
  padding: '6px 14px',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  background: '#fff',
  color: '#1e293b',
  fontSize: 14,
  cursor: 'pointer',
};

const COLORS = { departure: '#16a34a', arrival: '#dc2626', driver: '#2563eb' };

// The base URL of the tile endpoint is the map image URL without its query string. Browsers
// only ever talk to our API: tiles are fetched from the map provider and cached server-side.
const tileUrlFor = (serverMapUrl) => `${serverMapUrl.split('?')[0]}/tiles/{z}/{x}/{y}`;

function InteractiveMap({
  serverMapUrl, departureCoords, arrivalCoords, lastLocation, routeData,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const tilesRef = useRef(null);
  const layersRef = useRef(null);

  useEffect(() => {
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true });
    mapRef.current = map;
    tilesRef.current = L.tileLayer(tileUrlFor(serverMapUrl), {
      maxZoom: 17,
      minZoom: 3,
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> | Powered by <a href="https://www.geoapify.com/">Geoapify</a>',
    }).addTo(map);
    layersRef.current = L.layerGroup().addTo(map);
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // The map is created once; props are applied by the effects below.
  }, []);

  // The signed link is renewed regularly, tiles keep loading with the fresh one.
  useEffect(() => {
    tilesRef.current?.setUrl(tileUrlFor(serverMapUrl), true);
  }, [serverMapUrl]);

  const fitted = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    const layers = layersRef.current;
    if (!map || !layers) return;
    layers.clearLayers();

    const bounds = [];
    const addPoint = (lat, lon, color) => {
      bounds.push([lat, lon]);
      L.circleMarker([lat, lon], {
        radius: 9, color: '#fff', weight: 3, fillColor: color, fillOpacity: 1,
      }).addTo(layers);
    };
    if (routeData?.coordinates?.length > 1) {
      const line = routeData.coordinates.map((c) => [c.latitude, c.longitude]);
      L.polyline(line, { color: COLORS.driver, weight: 4 }).addTo(layers);
      bounds.push(...line);
    }
    if (departureCoords) addPoint(departureCoords.lat, departureCoords.lon, COLORS.departure);
    if (arrivalCoords) addPoint(arrivalCoords.lat, arrivalCoords.lon, COLORS.arrival);
    if (lastLocation) addPoint(lastLocation.latitude, lastLocation.longitude, COLORS.driver);

    // Frame the trip once; afterwards the viewer's own pan and zoom are left alone.
    if (!fitted.current) {
      if (bounds.length > 0) map.fitBounds(bounds, { padding: [30, 30] });
      else map.setView([47.5, 2.5], 5);
      fitted.current = true;
    }
  }, [departureCoords, arrivalCoords, lastLocation, routeData]);

  return <div ref={containerRef} style={MAP_STYLE} />;
}

function TripMap({
  departureCoords,
  arrivalCoords,
  lastLocation,
  routeData,
  mapUrl: serverMapUrl,
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const [interactive, setInteractive] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [serverMapUrl]);

  if (!serverMapUrl) {
    return (
      <div
        style={{
          width: '100%',
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          borderRadius: 8,
        }}
      >
        <span>Carte non disponible</span>
      </div>
    );
  }

  let view;
  if (interactive) {
    view = (
      <InteractiveMap
        serverMapUrl={serverMapUrl}
        departureCoords={departureCoords}
        arrivalCoords={arrivalCoords}
        lastLocation={lastLocation}
        routeData={routeData}
      />
    );
  } else if (imageFailed) {
    view = (
      <div style={UNAVAILABLE_STYLE}>
        <span>Carte non disponible</span>
      </div>
    );
  } else {
    // The default view is a single image the server renders once and shares between all
    // viewers: cheap for the map provider's daily limit.
    view = (
      <img
        src={serverMapUrl}
        alt="Carte du trajet"
        onError={() => setImageFailed(true)}
        style={{ ...MAP_STYLE, height: 'auto' }}
      />
    );
  }

  return (
    <div>
      {view}
      <button type="button" style={buttonStyle} onClick={() => setInteractive((value) => !value)}>
        {interactive ? 'Revenir à l\'image' : 'Carte interactive'}
      </button>
    </div>
  );
}

export default TripMap;
